import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { RunForm } from '../components/RunForm.js';
import { RunTimeline } from '../components/RunTimeline.js';
import { ScreenshotGallery } from '../components/Evidence.js';
import { LogViewer } from '../components/LogViewer.js';
import { Button, Callout, Card, PageHeader, StatusBadge } from '../components/primitives.js';
import { useRunStream } from '../lib/useRunStream.js';

/**
 * Post-Call Verification.
 *
 * A call can look perfect while it happens and still leave nothing useful
 * behind. This checks the Task record that the call produced: the right page
 * layout, the transcript, everything that was triggered, and the AI summary.
 */
export function PostCallPage(): ReactNode {
  const [runId, setRunId] = useState<string>();
  const stream = useRunStream(runId);
  const inProgress = stream.run?.outcome === 'running' || stream.run?.outcome === 'pending';

  return (
    <div className="stack">
      <PageHeader
        title="Post-call checks"
        lede="Open the Task record the call created and confirm everything persisted: the RocketPhone record page, the transcript, the triggered items, and the AI summary."
        actions={
          stream.run ? (
            <>
              <StatusBadge outcome={stream.run.outcome} />
              <Link className="button button--ghost button--small" to={`/runs/${stream.run.id}`}>
                Full report
              </Link>
            </>
          ) : null
        }
      />

      {!runId ? (
        <>
          <Callout tone="info">
            <div>
              Run this after a live call. It finds the most recent RocketPhone call task in the org
              and works through it.
            </div>
          </Callout>

          <RunForm
            stages={['post-call']}
            show={{}}
            submitLabel="Check the call record"
            onStarted={(run) => setRunId(run.id)}
          />

          <Card title="What gets checked">
            <ul className="stack stack--tight" style={{ paddingLeft: 20, margin: 0, fontSize: 14 }}>
              <li>The Task opens on the RocketPhone Call record page, not the standard one.</li>
              <li>The transcript is present and matches what was said.</li>
              <li>Bookmarks triggered during the call are still listed.</li>
              <li>Checklist items ticked during the call are still ticked.</li>
              <li>Call Assist items are still there.</li>
              <li>Call summary, sentiment and coaching tips are populated.</li>
              <li>The call appears in Call History on the Home page.</li>
            </ul>
          </Card>
        </>
      ) : (
        <>
          {stream.run?.reason ? (
            <Callout tone={stream.run.outcome === 'failed' ? 'danger' : 'warning'}>
              {stream.run.reason}
            </Callout>
          ) : null}

          <Card
            title="Results"
            actions={
              !inProgress ? (
                <Button size="small" onClick={() => setRunId(undefined)}>
                  Check again
                </Button>
              ) : null
            }
          >
            <RunTimeline runId={runId} steps={stream.run?.steps ?? []} />
          </Card>

          <Card title="Screenshots">
            <ScreenshotGallery
              runId={runId}
              evidence={(stream.run?.steps ?? []).flatMap((step) => step.evidence)}
            />
          </Card>

          <Card title="Output">
            <LogViewer logs={stream.logs} />
          </Card>
        </>
      )}
    </div>
  );
}
